import type { LiveHandshakeDto } from '@/core/data/handshake/handshake.interface';
import { gambits } from '@/core/gambits';
import { firestore } from '@/core/network/firebase';
import { useAuthStore } from '@/stores/auth.store';
import { GamStoreId } from '@/views/composables/constants/main/store.constants';
import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { defineStore, storeToRefs } from 'pinia';
import { ref, watch } from 'vue';

const REFRESH_DEBOUNCE_MS = 300;

/**
 * The signed-in player's handshakes inside their six hours, closed ones
 * included. Every screen that shows a live handshake or its game reads it from
 * here. Any change to a handshake touches both players' user docs, which
 * reloads this list.
 */
export const useLiveHandshakesStore = defineStore(GamStoreId.LIVE_HANDSHAKES, () => {
  const { user, firebaseUser } = storeToRefs(useAuthStore());

  const handshakes = ref<LiveHandshakeDto[]>([]);
  const isLoaded = ref(false);

  let unsubscribe: Unsubscribe | null = null;
  let watchedUserId: string | null = null;
  let inFlight: Promise<void> | null = null;
  let reloadAfterFlight = false;
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;

  const fetchOnce = async (userId: string) => {
    const result = await gambits.userService.getLiveHandshakes();
    if (watchedUserId !== userId || !result.data) return;

    handshakes.value = result.data;
    isLoaded.value = true;
  };

  /** Reload from the server. A call during a reload runs once more after it, so the last change always lands. */
  const load = (): Promise<void> => {
    const userId = watchedUserId;
    if (!userId) return Promise.resolve();

    if (inFlight) {
      reloadAfterFlight = true;
      return inFlight;
    }

    inFlight = (async () => {
      do {
        reloadAfterFlight = false;
        await fetchOnce(userId);
      } while (reloadAfterFlight && watchedUserId === userId);
    })().finally(() => {
      inFlight = null;
    });

    return inFlight;
  };

  const unlisten = () => {
    unsubscribe?.();
    unsubscribe = null;
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = null;
  };

  const stop = () => {
    unlisten();
    watchedUserId = null;
    reloadAfterFlight = false;
    handshakes.value = [];
    isLoaded.value = false;
  };

  /**
   * Reading the user doc needs Firebase signed in as this player, and Firestore
   * cancels a denied listener for good. So the listener waits for that sign-in,
   * which on a first registration lands after the list is already showing. Its
   * first snapshot reloads the list, catching whatever changed before it could hear.
   */
  const listen = () => {
    const userId = watchedUserId;
    if (!userId || unsubscribe || firebaseUser.value?.uid !== userId) return;

    unsubscribe = onSnapshot(
      doc(firestore, 'users', userId),
      () => {
        if (refreshTimer) clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => {
          refreshTimer = null;
          void load();
        }, REFRESH_DEBOUNCE_MS);
      },
      () => unlisten(),
    );
  };

  const start = (userId: string) => {
    stop();
    watchedUserId = userId;
    listen();
  };

  watch(
    () => user.value?.id ?? null,
    (userId) => {
      if (!watchedUserId) return;
      if (userId) start(userId);
      else stop();
    },
  );

  watch(
    () => firebaseUser.value?.uid ?? null,
    () => {
      unlisten();
      listen();
    },
  );

  /** Start listening for the signed-in player. Resolves once the list has loaded. */
  const ensure = async (): Promise<void> => {
    const userId = user.value?.id;
    if (!userId) return;

    if (watchedUserId !== userId) start(userId);
    if (!isLoaded.value) await load();
  };

  const byId = (handshakeId: string | null | undefined): LiveHandshakeDto | null =>
    (handshakeId && handshakes.value.find((handshake) => handshake.id === handshakeId)) || null;

  const withPeer = (peerId: string | null | undefined): LiveHandshakeDto[] =>
    peerId ? handshakes.value.filter((handshake) => handshake.peer?.id === peerId) : [];

  /** Keep what the game sheet just saved, so every screen shows it before the reload lands. */
  const patch = (handshakeId: string, change: Partial<LiveHandshakeDto>) => {
    handshakes.value = handshakes.value.map((handshake) =>
      handshake.id === handshakeId ? { ...handshake, ...change } : handshake,
    );
  };

  const close = async (handshakeId: string) => {
    const result = await gambits.userService.closeHandshake(handshakeId);
    if (!result.error) patch(handshakeId, { closedAt: new Date().toISOString() });
    void load();
    return result;
  };

  return { handshakes, isLoaded, ensure, load, byId, withPeer, patch, close };
});
